import { ReactElement } from 'react';
import { Droppable } from 'react-beautiful-dnd';
import { ThreadMessage } from '@screentone/addon-auth-wrapper';
import { FetchNextPageOptions, InfiniteQueryObserverResult } from '@tanstack/react-query';
import classnames from 'classnames';

import { CollectionCard } from 'components/collection-card/CollectionCard';
import { InfiniteScrollWaypoint } from 'components/infinite-scroll-waypoint/InfiniteScrollWaypoint';
import { NoSearchResults } from 'components/no-search-results/NoSearchResults';
import { getPageLockThreadId, PageLockProvider } from 'contexts/page-lock/PageLockContext';
import { AvailableFeatureFlags, CollectionDto, CollectionsDtoQuery } from 'data/generated/graphql';
import { useElementWidth } from 'hooks/dom';
import { AllessehContentQueryResponse } from 'hooks/useAllessehContentQuery';
import styles from './CollectionsList.module.scss';

interface CollectionsListProps {
  className?: string;
  collectionPages: CollectionsDtoQuery[];
  emptyListMessage?: string;
  renderCollectionCardWrapper?: (
    collectionCard: ReactElement,
    collection: CollectionDto,
    index: number
  ) => ReactElement;
  droppableId?: string;
  nextPageProps: {
    fetchNextPage: (
      options?: FetchNextPageOptions | undefined
    ) => Promise<InfiniteQueryObserverResult<CollectionsDtoQuery | AllessehContentQueryResponse>>;
    isFetchingNextPage: boolean;
    hasNextPage?: boolean;
  };
  initialThreadData: Record<string, ThreadMessage[]>;
}

export const CollectionsList = ({
  className,
  collectionPages,
  emptyListMessage,
  renderCollectionCardWrapper,
  droppableId,
  nextPageProps,
  initialThreadData
}: CollectionsListProps) => {
  const { elementRef, elementWidth } = useElementWidth();

  if (collectionPages.length === 0 || collectionPages[0].collectionsDTO.edges.length === 0) {
    return <NoSearchResults title="Collections" bodyText={emptyListMessage} />;
  }

  const collections: CollectionDto[] = collectionPages.reduce((acc: CollectionDto[], curr: CollectionsDtoQuery) => {
    const collections = curr.collectionsDTO.edges.map((collectionEdge) => {
      const { node: collection } = collectionEdge;
      return collection;
    });
    return acc.concat(collections as CollectionDto[]);
  }, []);

  const list = (
    <div
      ref={elementRef}
      className={classnames(styles.collectionList, droppableId ? '' : className)}
      data-testid="collection-list-container"
    >
      {collections.map((collection, index) => {
        const threadId = getPageLockThreadId(collection.metadata.allessehId);
        const isReady = threadId in initialThreadData;
        return (
          <PageLockProvider
            key={`${collection.idCreatedUtc}-${isReady ? 'ready' : 'loading'}`}
            allessehId={collection.metadata.allessehId}
            fragments={['publishing']}
            featureFlag={AvailableFeatureFlags.CollectionLockingEnabled}
            skipInitialFetch
            initialMessages={initialThreadData[threadId]}
          >
            <CollectionCard
              collection={collection}
              containerWidth={elementWidth}
              index={index}
              isOnClickEnabled={!droppableId}
              renderCollectionCardWrapper={renderCollectionCardWrapper}
            />
          </PageLockProvider>
        );
      })}
      <InfiniteScrollWaypoint nextPageProps={nextPageProps} />
    </div>
  );

  if (droppableId) {
    return (
      <Droppable droppableId={droppableId} isDropDisabled>
        {(droppableProvided) => (
          <div ref={droppableProvided.innerRef} {...droppableProvided.droppableProps} className={className}>
            {list}
            {droppableProvided.placeholder}
          </div>
        )}
      </Droppable>
    );
  }

  return list;
};
